import { useState, useEffect } from 'react';
import { KTSVG, toAbsoluteUrl } from '../../../_metronic/helpers';
import { useNavigate } from 'react-router-dom';
import { Button, FormControl } from 'react-bootstrap';
import { BalanceCard, loginLogout, useProjectByStorage } from '../projectData';
import { getCart, updatedCartItem } from '../api';
import { FullPageLoader } from '../../component/helper';
import { useRouteData } from '../RouteDataContext';
import { swlAlert } from '../../component/sweetAlert';
import OrderReconfirmCart from './orderReconfirmCart';

/**
 * CartDetail
 * Renders the user's cart page, displays cart items, and handles quantity updates and reconfirmation.
 */
const CartDetail = () => {
    const navigate = useNavigate();
    const project = useProjectByStorage();
    const { userData, dashboard, getUserData } = useRouteData();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reconfirm, setReconfirm] = useState(false);

    useEffect(() => {
        if (project) {
            getCart({ project_key: project?.pkey })
                .then((response) => {
                    if (response.code === 0) throw new Error('Failed to fetch cart');
                    setCartItems(response?.data || []);
                })
                .catch((err) => {
                    setError(err.message);
                    loginLogout(err.response)
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [project]);

    /**
     * updateQuantity
     * Updates the quantity of a cart item, removes item if quantity is zero, and refreshes user data.
     */
    const updateQuantity = async (row_id, quantity) => {
        if (isNaN(quantity) || quantity < 0) return;

        try {
            setLoading(true);
            const d = await updatedCartItem({ row_id, quantity });

            if (d.code === 1) {
                if (quantity === 0) {
                    setCartItems((prev) => prev.filter((item) => item.row_id !== row_id));
                } else {
                    setCartItems((prev) =>
                        prev.map((item) =>
                            item.row_id === row_id ? { ...item, quantity } : item
                        )
                    );
                }
                getUserData(userData?.user?.iirpl_sap_code, project?.pkey);
            } else {
                swlAlert(d);
            }

            setTimeout(() => setLoading(false), 800);
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div className="container vh-100 bg-white pt-4">
            {!reconfirm && (
                <>
                    <div className="card px-2 mb-4 border rounded" style={{ backgroundColor: "#FBF6F7", border: "1px solid #FBF6F7" }}>
                        <div className="card-body p-3 d-flex justify-content-between align-items-center">
                            <div className="fw-semibold fs-5">
                                <span style={{ color: "#22A127" }} className="text-danger fw-semibold">
                                    {cartItems?.length}
                                </span>{' '}
                                items
                            </div>
                            <div>
                                <button onClick={() => navigate("/customer/redem-category")} className="btn btn-sm btn-primary">
                                    Add more items <i className="bi bi-chevron-right fs-6"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="notice d-flex bg-light-danger rounded border-danger border border-dashed p-2 mt-5 mb-4">
                            <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-3x svg-icon-danger me-5' />
                            <div className="d-flex flex-stack flex-grow-1 flex-wrap flex-md-nowrap">
                                <div className="mb-md-0 fw-semibold">
                                    <div className="fs-7 text-gray-700 pe-7">{error}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {cartItems.length === 0 ? (
                        <div className="card p-3 mb-4 shadow-sm" style={{ border: '1px solid #eaeaea' }}>
                            Your cart is empty.
                        </div>
                    ) : (
                        <>
                            {cartItems.map((item) => (
                                <div key={item.row_id} className="card p-3 mb-4 rounded shadow-sm" style={{ border: '1px solid #eaeaea' }}>
                                    <div className="d-flex">
                                        <img
                                            src={item?.image_url ?? toAbsoluteUrl('/media/logos/blank-big.jpg')}
                                            alt="product"
                                            className="img-fluid h-100px rounded me-4"
                                            style={{ height: '112px', width: '112px' }}
                                        />

                                        <div className="d-flex flex-column gap-2">
                                            <div className="fs-7">{item?.sku_name}</div>
                                            <div className="d-flex gap-3 justify-content-between">
                                                <div>
                                                    <span className="fw-semibolder text-danger">{item?.redeemed_point} Pts</span>
                                                </div>
                                            </div>

                                            <div className="d-flex gap-4">
                                                <div className="border w-55 rounded">
                                                    <div className="d-flex rounded">
                                                        <Button className="fs-6 text-dark rounded-0 rounded-start" variant="outline-secondary" onClick={() => updateQuantity(item.row_id, item.quantity - 1)}>-</Button>
                                                        <FormControl
                                                            value={item.quantity}
                                                            onChange={(e) => updateQuantity(item.row_id, parseInt(e.target.value, 10))}
                                                            type="number"
                                                            min="1"
                                                            className="form-control-solid w-60px fs-7 rounded-0 text-center"
                                                        />
                                                        <Button className="fs-6 text-dark rounded-0 rounded-end" variant="outline-secondary" onClick={() => updateQuantity(item.row_id, item.quantity + 1)}>+</Button>
                                                    </div>
                                                </div>
                                                <span className="w-[40px] h-[40px] rounded p-3 d-flex justify-center items-center bg-gray-100 cursor-pointer" onClick={() => updateQuantity(item.row_id, 0)}>
                                                    <i className="bi fs-2 mt-1 text-danger bi-trash-fill"></i>
                                                </span>
                                            </div>

                                            {item?.total_redeemed && (
                                                <div>
                                                    Total Used Points: <span className="fw-semibolder text-success">{item?.redeemed_point * item.quantity} Pts</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="card shadow-sm p-2 bg-white mb-4 border rounded" style={{ backgroundColor: "#FBF6F7", border: "1px solid #FBF6F7" }}>
                                <div className="d-flex justify-content-between p-3">
                                    <div className="fw-semibolder fs-5">This Redemption</div>
                                    <div className="fw-semibolder fs-5">{dashboard?.this_cart_point} pts</div>
                                </div>
                            </div>

                            <BalanceCard totalBalance={dashboard?.totalBalance} totalPoints={dashboard?.this_cart_point} />
                        </>
                    )}

                    {cartItems.length > 0 && (
                        <div onClick={() => setReconfirm(true)} className="sticky-bottom mt-4">
                            <div className="rounded-5 bg-gray-100 w-100">
                                <button className="btn btn-danger btn-sm fs-3 w-100">Redeem</button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {loading && <FullPageLoader />}
            {reconfirm && (
                <>
                    <OrderReconfirmCart
                        mobile={userData?.user?.phone}
                        project_key={project?.pkey}
                        dashboard={dashboard}
                        cartItems={cartItems}
                        setReconfirm={setReconfirm}
                    />

                </>
            )}

        </div>
    );
};

export default CartDetail;



